// src/pages/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OrganizationSettings from '../components/organization/OrganizationSettings';
import TeamManagement from '../components/organization/TeamManagement';
import UpgradePlan from '../components/organization/UpgradePlan';
import UsageMeter from '../components/organization/UsageMeter';
import BillingSection from '../components/organization/BillingSection';
import { useTranslation } from 'react-i18next';
import {
    Cog6ToothIcon, UserGroupIcon, CreditCardIcon, BoltIcon
} from '@heroicons/react/24/outline';

const TABS = [
    { id: 'organization', label: 'Organizzazione', icon: Cog6ToothIcon, description: 'Dati aziendali e preferenze' },
    { id: 'team', label: 'Team', icon: UserGroupIcon, description: 'Gestione membri e inviti' },
    { id: 'plan', label: 'Piano', icon: BoltIcon, description: 'Abbonamento e limiti' },
    { id: 'billing', label: 'Fatturazione', icon: CreditCardIcon, description: 'Pagamenti e fatture' },
];

const SettingsPage = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'organization');
    const { organization, subscription, isSuperAdmin } = useAuth();

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && TABS.find(t => t.id === tab)) setActiveTab(tab);
    }, [searchParams]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setSearchParams({ tab: tabId });
    };

    const activeTabData = TABS.find(t => t.id === activeTab);

    return (
        <div className="max-w-6xl mx-auto space-y-6">

            <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('nav.settings')}</h1>
                <p className="text-gray-500 mt-1 text-sm">Gestisci la tua organizzazione, team e abbonamento</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">

                {/* Sidebar */}
                <div className="lg:w-60 flex-shrink-0">
                    <nav className="space-y-1">
                        {TABS.map(tab => {
                            const Icon = tab.icon;
                            const active = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${active ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                >
                                    <Icon className="w-5 h-5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium leading-tight">{tab.label}</p>
                                        <p className={`text-xs leading-tight mt-0.5 truncate ${active ? 'text-blue-200' : 'text-gray-400'}`}>
                                            {tab.description}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </nav>

                    {/* Info piano */}
                    {organization && (
                        <div className="mt-5 p-4 bg-gray-50 rounded-xl border">
                            <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">Piano Attivo</p>
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${subscription?.status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-yellow-400'}`} />
                                <span className="text-sm font-bold text-gray-900">{subscription?.plan || organization.plan}</span>
                            </div>
                            {isSuperAdmin && (
                                <div className="mt-2">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                        👑 SuperAdmin
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Contenuto */}
                <div className="flex-1 min-w-0">
                    {/* Header tab */}
                    <div className="bg-white rounded-2xl border px-6 py-4 mb-5 flex items-center gap-3">
                        {activeTabData && (
                            <>
                                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <activeTabData.icon className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-gray-900">{activeTabData.label}</h2>
                                    <p className="text-xs text-gray-400">{activeTabData.description}</p>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Tab content */}
                    {activeTab === 'organization' && <OrganizationSettings />}
                    {activeTab === 'team' && <TeamManagement />}
                    {activeTab === 'plan' && (
                        <div className="space-y-5">
                            <UsageMeter />
                            <UpgradePlan />
                        </div>
                    )}
                    {activeTab === 'billing' && <BillingSection />}
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
